import { useEffect, useState, useRef } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import api, { eur, num } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ProfilazioneB2B from "@/components/ProfilazioneB2B";
import ActivityLog from "@/components/ActivityLog";
import QuickAdd from "@/components/QuickAdd";
import VisitaB2BDialog from "@/components/VisitaB2BDialog";
import { ArrowLeft, Gauge, Loader2, CalendarClock, Download, Gift, ShieldCheck, ClipboardCheck, Users, Wallet, Sparkles, Upload, FileText, AlertTriangle, Handshake, MapPin } from "lucide-react";
import ConvertPartnerDialog from "@/components/ConvertPartnerDialog";
import PinPicker from "@/components/PinPicker";
import VestaInfo from "@/components/VestaInfo";
import VestaAccessCard from "@/components/VestaAccessCard";
import VestaBadge from "@/components/VestaBadge";
import ReferentiEditor from "@/components/ReferentiEditor";
import { mergeReferenti } from "@/lib/referenti";
import { QuotesForClient } from "@/components/QuotesForClient";
import ConsulenzeAnagrafica from "@/components/ConsulenzeAnagrafica";
import ConsulenzeTecnicheAnagrafica from "@/components/ConsulenzeTecnicheAnagrafica";
import { AiProfiloCrm, ProfiloExtra } from "@/components/AiProfiloCrm";
import ExtraScontoCard from "@/components/ExtraScontoCard";
import WebProfileCard from "@/components/webprofile/WebProfileCard";

const STATO_STYLE = {
  "In linea": "bg-green-100 text-green-800 border-green-300",
  "Attenzione": "bg-amber-100 text-amber-800 border-amber-300",
  "Fuori presidio": "bg-red-100 text-red-700 border-red-300",
  "N/D": "bg-muted text-muted-foreground border-border",
};

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between border-b border-border py-2 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-semibold">{value ?? "—"}</span>
  </div>
);

const SubHead = ({ children }) => (
  <h3 className="mb-1 mt-6 font-mono text-[11px] font-bold uppercase tracking-widest text-primary first:mt-2">{children}</h3>
);

const FidoBox = ({ titolo, sug, onApply, applying }) => {
  if (!sug) return null;
  const isCs = titolo === "creditsafe";
  const bad = !!sug.non_affidabile;
  const label = bad ? "NON AFFIDABILE" : (sug.rinnovo_obbligatorio ? "rinnovo obbligatorio" : (sug.applicabile && sug.fido_auto != null ? eur(sug.fido_auto) : "verifica manuale"));
  return (
    <div className={`rounded-lg border p-3 ${bad ? "border-red-500/60 bg-red-500/10" : "border-primary/30 bg-primary/5"}`} data-testid={`fido-box-${titolo}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <span className={`text-[11px] font-bold uppercase tracking-wide ${bad ? "text-red-500" : "text-primary"}`}>{isCs ? "Da Creditsafe" : "Da bilancio"}</span>
          <div className={`font-heading text-lg font-bold ${bad ? "text-red-600 dark:text-red-400" : ""}`}>{label}</div>
        </div>
        {sug.applicabile && sug.fido_auto != null && (
          <Button size="sm" variant="outline" onClick={() => onApply(sug.fido_auto)} disabled={applying} data-testid={`fido-applica-${titolo}`}>Applica</Button>
        )}
      </div>
      {isCs ? (
        <>
          {(sug.rischio || sug.score != null) && <p className="mt-1 text-[11px] font-semibold">Valutazione: {sug.rischio || "—"}{sug.score != null ? ` · score ${sug.score}` : ""}</p>}
          {(sug.data_fondazione || sug.dipendenti != null) && <p className="mt-0.5 text-[11px] text-muted-foreground">{sug.data_fondazione ? `Fondazione ${sug.data_fondazione.includes && sug.data_fondazione.includes("-") ? sug.data_fondazione.split("-").reverse().join("/") : sug.data_fondazione}` : ""}{sug.data_fondazione && sug.dipendenti != null ? " · " : ""}{sug.dipendenti != null ? `${sug.dipendenti} dip.` : ""}</p>}
          <p className="mt-1 text-[11px] text-muted-foreground">{sug.note}</p>
          {sug.data_registrazione && (
            <p className={`mt-1 flex items-center gap-1 text-[11px] ${sug.troppo_vecchio ? "font-semibold text-amber-600 dark:text-amber-400" : "text-muted-foreground"}`} data-testid="cs-data-registrazione">
              {sug.troppo_vecchio && <AlertTriangle className="h-3 w-3 shrink-0" />}
              Dato registrato il {new Date(sug.data_registrazione).toLocaleDateString("it-IT")}{sug.eta_mesi != null ? ` · ${sug.eta_mesi} mesi fa` : ""}{sug.troppo_vecchio ? " — da aggiornare (oltre 6 mesi)" : ""}
            </p>
          )}
        </>
      ) : sug.breakdown ? (
        <p className="mt-1 text-[11px] text-muted-foreground">10% fatturato ({eur(sug.breakdown.base_fatturato)}) · tetto 50% patrimonio ({eur(sug.breakdown.tetto_patrimonio)}) · coeff. rischio {sug.breakdown.coeff_rischio} ({sug.breakdown.coeff_desc}) · max {eur(200000)}</p>
      ) : null}
      {!isCs && sug.rinnovo_obbligatorio ? (
        <div className="mt-2 flex items-center gap-2 rounded-md bg-red-500/15 px-2 py-1 text-[11px] font-semibold text-red-600 dark:text-red-400">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Rinnovo obbligatorio: bilancio di {sug.eta_mesi} mesi. Carica un bilancio aggiornato.
        </div>
      ) : !isCs && sug.bilancio_vecchio ? (
        <div className="mt-2 flex items-center gap-2 rounded-md bg-amber-500/15 px-2 py-1 text-[11px] font-semibold text-amber-600 dark:text-amber-400">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Bilancio di {sug.eta_mesi} mesi — fido ridotto del {sug.riduzione_pct}%. Carica un bilancio aggiornato.
        </div>
      ) : null}
    </div>
  );
};

const Kpi = ({ label, value, highlight, testid }) => (
  <div className={`rounded-md p-3 ${highlight ? "border-2 border-primary/40 bg-primary/5" : "bg-muted/50"}`}>
    <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className={`mt-0.5 font-heading text-xl font-black ${highlight ? "text-primary" : ""}`} data-testid={testid}>{value}</div>
  </div>
);

export default function PartnerDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const canConvert = ["admin", "commercial_manager", "back_office", "head_of_partner"].includes(user?.role);
  const canAff = ["admin", "back_office"].includes(user?.role) || user?.role_actual === "back_office" || (user?.extra_pages || []).includes("backoffice");
  const canEditLogo = ["admin", "head_of_partner"].includes(user?.role);
  const canExtra = ["admin", "head_of_partner", "back_office"].includes(user?.role) || user?.role_actual === "back_office";
  const [sconfinato, setSconfinato] = useState(false);
  const [p, setP] = useState(null);
  const [presidio, setPresidio] = useState(null);
  const [cstats, setCstats] = useState(null);
  const [period, setPeriod] = useState("mese");
  const [refreshKey, setRefreshKey] = useState(0);
  const [logoSrc, setLogoSrc] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoVersion, setLogoVersion] = useState(0);
  const logoFileRef = useRef(null);
  const [prenotazioni, setPrenotazioni] = useState(null);
  const [visitaOpen, setVisitaOpen] = useState(false);
  const [refs, setRefs] = useState([]);
  const [refsSaving, setRefsSaving] = useState(false);
  const [affVal, setAffVal] = useState("");
  const [affSaving, setAffSaving] = useState(false);
  const [bilUploading, setBilUploading] = useState(false);
  const [fidoSug, setFidoSug] = useState(null);
  const [csMan, setCsMan] = useState({ score: "", fido_consigliato: "", data_fondazione: "", dipendenti: "" });
  const [csManSaving, setCsManSaving] = useState(false);
  const bilFileRef = useRef(null);
  const [csEnriching, setCsEnriching] = useState(false);
  const [payMethods, setPayMethods] = useState([]);
  const [payDefault, setPayDefault] = useState("__none__");
  const [paySaving, setPaySaving] = useState(false);
  const [convDlg, setConvDlg] = useState(null);
  useEffect(() => { if (canAff) api.get("/payment-methods").then((r) => setPayMethods(r.data.methods || [])).catch(() => {}); }, [canAff]);
  useEffect(() => { if (p) setPayDefault(p.modalita_pagamento || "__none__"); }, [p]);
  const savePayDefault = async (val) => {
    setPayDefault(val);
    setPaySaving(true);
    try { await api.put(`/partners/${id}/pagamento-default`, { modalita: val === "__none__" ? "" : val }); toast.success("Modalità di pagamento di default salvata"); loadPartner(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setPaySaving(false); }
  };

  const saveAffidamento = async () => {
    setAffSaving(true);
    try { await api.put(`/partners/${id}/affidamento`, { affidamento_max: affVal === "" ? null : affVal }); toast.success("Fido massimo aggiornato"); loadPartner(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setAffSaving(false); }
  };
  const applyFido = async (value) => {
    setAffVal(String(value));
    setAffSaving(true);
    try { await api.put(`/partners/${id}/affidamento`, { affidamento_max: value }); toast.success("Fido suggerito applicato"); loadPartner(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setAffSaving(false); }
  };
  const saveCsManual = async () => {
    setCsManSaving(true);
    try { await api.put(`/partners/${id}/creditsafe-manual`, csMan); toast.success("Dati Creditsafe salvati"); loadPartner(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setCsManSaving(false); }
  };
  const uploadBilancio = async (file) => {
    if (!file) return;
    setBilUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/partners/${id}/bilancio-upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Bilancio caricato e dati acquisiti automaticamente");
      loadPartner();
    } catch (e) { toast.error(e.response?.data?.detail || "Caricamento non riuscito"); }
    finally { setBilUploading(false); }
  };
  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/partners/${id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo caricato");
      await loadPartner();
      setLogoVersion((v) => v + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Caricamento logo non riuscito"); }
    finally { setLogoUploading(false); }
  };
  const removeLogo = async () => {
    if (!window.confirm("Rimuovere il logo del partner?")) return;
    setLogoUploading(true);
    try {
      await api.delete(`/partners/${id}/logo`);
      setLogoSrc(null);
      toast.success("Logo rimosso");
      await loadPartner();
      setLogoVersion((v) => v + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Rimozione non riuscita"); }
    finally { setLogoUploading(false); }
  };
  const downloadBilancio = async (fileId) => {
    try {
      const r = await api.get(`/partners/${id}/bilancio-file/${fileId}`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("Download non riuscito"); }
  };
  const enrichCreditsafe = async () => {
    setCsEnriching(true);
    try {
      const r = await api.post(`/partners/${id}/creditsafe-enrich`);
      const pt = r.data?.partner || {};
      if (r.data?.report_access) {
        const parts = [];
        if (pt.fascia_rischio || pt.score != null) parts.push(`valutazione ${pt.fascia_rischio || "—"}${pt.score != null ? ` (score ${pt.score})` : ""}`);
        if (pt.creditsafe_credit_limit != null) parts.push(`fido consigliato ${eur(pt.creditsafe_credit_limit)}`);
        toast.success(`Creditsafe — ${parts.join(" · ") || "report aggiornato"}`);
      } else {
        toast.warning("Creditsafe: solo anagrafica — l'utente API non ha accesso ai report IT (403). Chiedi a Creditsafe di abilitare i report IT sull'utenza API.");
      }
      loadPartner();
    }
    catch (e) { toast.error(e.response?.data?.detail || "Creditsafe non disponibile"); }
    finally { setCsEnriching(false); }
  };

  const saveRefs = async () => {
    setRefsSaving(true);
    try { await api.put(`/partners/${id}/referenti`, { referenti: refs }); toast.success("Referenti salvati"); loadPartner(); }
    catch { toast.error("Salvataggio referenti non riuscito"); }
    finally { setRefsSaving(false); }
  };

  const loadPartner = () => api.get(`/partners/${id}`).then((r) => setP(r.data));
  const scaricaContrattoFirmato = async (fileId, filename) => {
    try {
      const r = await api.get(`/partners/${id}/contratto-partner/${fileId}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = filename || "contratto.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Impossibile scaricare il contratto"); }
  };

  const location = useLocation();
  const [pin, setPin] = useState({ lat: null, lng: null });
  const [pinSaving, setPinSaving] = useState(false);
  useEffect(() => { if (p) setPin({ lat: p.lat ?? null, lng: p.lng ?? null }); }, [p]);
  useEffect(() => {
    if (location.hash === "#mappa-pin" && p) {
      const t = setTimeout(() => document.getElementById("mappa-pin")?.scrollIntoView({ behavior: "smooth", block: "start" }), 350);
      return () => clearTimeout(t);
    }
  }, [location.hash, p]);
  const savePin = async () => {
    setPinSaving(true);
    try { await api.put(`/partners/${id}/posizione`, { lat: pin.lat, lng: pin.lng }); toast.success("Posizione aggiornata"); loadPartner(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio posizione"); }
    finally { setPinSaving(false); }
  };
  useEffect(() => { loadPartner(); }, [id]);
  useEffect(() => {
    if (canAff) api.get("/crm-quotes/affidamenti-sconfinati").then((r) => setSconfinato((r.data.ids || []).includes(id))).catch(() => {});
  }, [id, canAff]);
  useEffect(() => {
    if (canAff && p) api.get(`/partners/${id}/fido-suggerito`).then((r) => setFidoSug(r.data)).catch(() => setFidoSug(null));
  }, [id, canAff, p]);
  useEffect(() => {
    const m = p?.creditsafe_manual || {};
    setCsMan({ score: m.score ?? "", fido_consigliato: m.fido_consigliato ?? "", data_fondazione: m.data_fondazione ?? "", dipendenti: m.dipendenti ?? "" });
  }, [p?.creditsafe_manual]);
  useEffect(() => { if (p) setRefs(p.referenti || []); }, [p]);
  const referentiAll = mergeReferenti(p);
  useEffect(() => {
    if (!p) return;
    const fd = p.fier_dettaglio || {};
    setAffVal(p.affidamento_max != null ? p.affidamento_max : (fd.fido_massimo != null ? fd.fido_massimo : ""));
  }, [p]);
  useEffect(() => { api.get(`/partners/${id}/presidio?period=${period}`).then((r) => setPresidio(r.data)); }, [id, period]);
  useEffect(() => { api.get(`/partners/${id}/commercial-stats`).then((r) => setCstats(r.data)).catch(() => {}); }, [id, refreshKey]);
  useEffect(() => { api.get(`/partners/${id}/prenotazioni`).then((r) => setPrenotazioni(r.data || [])).catch(() => setPrenotazioni([])); }, [id, refreshKey]);
  useEffect(() => {
    if (!p?.has_logo) { setLogoSrc(null); return; }
    let url;
    api.get(`/partners/${id}/logo`, { responseType: "blob" }).then((r) => {
      url = window.URL.createObjectURL(r.data);
      setLogoSrc(url);
    }).catch(() => {});
    return () => { if (url) window.URL.revokeObjectURL(url); };
  }, [id, p?.has_logo, logoVersion]);

  if (!p) return <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const sow = p.share_of_wallet;
  const cs = p.creditsafe || {};
  const indirizzo = p.address || cs.address || null;
  const anagCitta = [p.cap, p.city || cs.city].filter(Boolean).join(" ") || null;
  const ateco = p.ateco || cs.activity_code || null;
  const emailsExtra = (p.emails || []).filter((e) => e && e !== p.email);
  const csDate = cs.updated_at ? new Date(cs.updated_at).toLocaleDateString("it-IT") : null;
  const csStato = cs.status ? `Verificata${csDate ? ` · agg. ${csDate}` : ""}` : null;
  const fd = p.fier_dettaglio || {};
  const itDate = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : null);
  const scaricaContratto = async () => {
    try {
      const r = await api.get(`/partners/${id}/contratto`, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl; a.download = fd.contratto_nome || "contratto.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) { toast.error("Impossibile scaricare il contratto dal gestionale"); }
  };

  return (
    <div className="space-y-6" data-testid="partner-detail-page">
      <Link to="/partners" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="back-partners">
        <ArrowLeft className="h-4 w-4" /> Clienti
      </Link>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <div className="flex flex-col items-center gap-1">
            {logoSrc
              ? <img src={logoSrc} alt="Logo partner" className="h-16 w-16 shrink-0 rounded-md border border-border bg-white object-contain p-1" data-testid="partner-logo" />
              : (canEditLogo && <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md border border-dashed border-border text-center text-[10px] leading-tight text-muted-foreground" data-testid="partner-logo-empty">Nessun<br />logo</div>)}
            {canEditLogo && (
              <div className="flex items-center gap-1">
                <input ref={logoFileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" data-testid="logo-upload-input"
                  onChange={(e) => { uploadLogo(e.target.files?.[0]); e.target.value = ""; }} />
                <button onClick={() => logoFileRef.current?.click()} disabled={logoUploading} className="inline-flex items-center gap-1 text-[11px] font-semibold text-foreground transition-colors hover:text-primary disabled:opacity-40" data-testid="logo-upload-btn">
                  {logoUploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}{logoSrc ? "Cambia" : "Carica logo"}
                </button>
                {logoSrc && (<><span className="text-[11px] text-muted-foreground">·</span>
                  <button onClick={removeLogo} disabled={logoUploading} className="text-[11px] font-semibold text-foreground transition-colors hover:text-destructive disabled:opacity-40" data-testid="logo-remove-btn">Rimuovi</button></>)}
              </div>
            )}
          </div>
          <div>
            <h1 className="font-heading text-3xl font-black tracking-tight">{p.name}</h1>
            {sconfinato && <Badge className="bg-red-600 text-white" data-testid="partner-sconfino-badge">Oltre fido</Badge>}
            <p className="text-sm text-muted-foreground">{p.city} · {p.region || "N/D"} · {p.agency_name || "Nessuna agenzia"}</p>
          </div>
        </div>
        {p.partner_class && <Badge className="text-sm">Classe {p.partner_class}</Badge>}
        {p.area_riservata_attiva && (
          <Badge className="gap-1.5 border-emerald-300 bg-emerald-100 text-sm text-emerald-800" data-testid="area-riservata-badge">
            <ShieldCheck className="h-4 w-4" />
            Area riservata attiva
            {p.area_riservata_updated_at && (
              <span className="font-normal opacity-80">· agg. {itDate(p.area_riservata_updated_at)}</span>
            )}
          </Badge>
        )}
        <Link to={`/configuratore/${p.id}`} data-testid="open-configuratore-link">
          <Button data-testid="open-configuratore-btn">Apri nel Configuratore</Button>
        </Link>
      </div>

      <div className="flex flex-wrap justify-end gap-2">
        <QuickAdd partnerId={id} partnerName={p.name} agencyId={p.agency_id} onChanged={() => { loadPartner(); setRefreshKey((k) => k + 1); }} show={["activity"]} />
        <Button variant="outline" size="sm" onClick={() => setVisitaOpen(true)} data-testid="partner-registra-visita-btn">
          <ClipboardCheck className="mr-2 h-4 w-4" /> Registra visita
        </Button>
      </div>
      <VisitaB2BDialog open={visitaOpen} onOpenChange={setVisitaOpen} partners={[{ id: p.id, name: p.name }]} prospects={[]} defaultPartnerId={p.id} onSaved={() => { loadPartner(); setRefreshKey((k) => k + 1); }} />

      {canExtra && <ExtraScontoCard partner={p} onChanged={loadPartner} />}

      <Card className="p-6" data-testid="partner-referenti-card">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          <h2 className="font-heading text-lg font-bold tracking-tight">Referenti e Contatti</h2>
          {referentiAll.length > 0 && <Badge variant="secondary" className="text-[11px]" data-testid="referenti-count">{referentiAll.length}</Badge>}
          <span className="ml-auto text-[11px] text-muted-foreground">Fier + inseriti a mano</span>
        </div>
        {referentiAll.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="referenti-empty">Nessun referente per questo cliente.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="py-2 pr-3 font-medium">Nominativo</th>
                  <th className="py-2 pr-3 font-medium">Ruolo</th>
                  <th className="py-2 pr-3 font-medium">Email</th>
                  <th className="py-2 pr-3 font-medium">Cellulare</th>
                  <th className="py-2 font-medium">Telefono fisso</th>
                </tr>
              </thead>
              <tbody>
                {referentiAll.map((c, i) => (
                  <tr key={i} className="border-b border-border/50" data-testid={`referente-row-${i}`}>
                    <td className="py-2 pr-3 font-semibold">
                      {c.foto_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${c.foto_url}`} alt="" className="mr-2 inline-block h-8 w-8 rounded-full object-cover align-middle" data-testid={`referente-foto-${i}`} />}
                      {c.nome || "—"}
                      {c.principale && <Badge className="ml-2 border-emerald-300 bg-emerald-100 text-[10px] text-emerald-800">Principale</Badge>}
                      {c.source === "crm" && <Badge variant="outline" className="ml-2 text-[10px]">CRM</Badge>}
                    </td>
                    <td className="py-2 pr-3 text-muted-foreground">{c.ruolo || "—"}</td>
                    <td className="py-2 pr-3">{c.email ? <a href={`mailto:${c.email}`} className="text-primary hover:underline" data-testid={`referente-email-${i}`}>{c.email}</a> : "—"}</td>
                    <td className="py-2 pr-3 font-mono">{c.tel_cellulare || "—"}</td>
                    <td className="py-2 font-mono">{c.tel_fisso || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <SubHead>Referenti aggiunti manualmente</SubHead>
        <ReferentiEditor value={refs} onChange={setRefs} testid="partner-referenti-editor" />
        <div className="mt-3 flex justify-end">
          <Button size="sm" onClick={saveRefs} disabled={refsSaving} data-testid="partner-referenti-save">
            {refsSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salva referenti
          </Button>
        </div>
      </Card>

      <ProfilazioneB2B partner={p} onRefresh={loadPartner} />

      <Card className="p-6" data-testid="partner-commercial-card">
        <h2 className="mb-4 font-heading text-lg font-bold tracking-tight">Andamento commerciale · Preventivi & Ordini</h2>
        {p.is_partner && (
          <div className="mb-4 flex items-center justify-between rounded-lg border-2 border-amber-400/60 bg-amber-400/10 p-4" data-testid="vesta-slot">
            <div className="flex items-center gap-3">
              <VestaBadge className="h-11 w-11" />
              <div>
                <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#E3B341]">Saldo Vesta <VestaInfo /></div>
                <div className="text-xs text-muted-foreground">Guadagnati {cstats?.vesta_punti != null ? cstats.vesta_punti.toLocaleString("it-IT", { maximumFractionDigits: 1 }) : "—"} · Spesi {cstats?.vesta_spesi != null ? cstats.vesta_spesi.toLocaleString("it-IT", { maximumFractionDigits: 1 }) : "—"}</div>
              </div>
            </div>
            <div className="font-heading text-4xl font-black text-[#E3B341]" data-testid="vesta-value">{cstats?.vesta_saldo != null ? cstats.vesta_saldo.toLocaleString("it-IT", { maximumFractionDigits: 1 }) : "—"}</div>
          </div>
        )}
        {!cstats ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <Kpi label="N° preventivi" value={num(cstats.n_preventivi || 0)} testid="cs-n-preventivi" />
            <Kpi label="Importo preventivi" value={eur(cstats.importo_preventivi || 0)} testid="cs-importo-preventivi" />
            <Kpi label="N° ordini" value={num(cstats.n_ordini || 0)} testid="cs-n-ordini" />
            <Kpi label="Totale ordini" value={eur(cstats.tot_ordini || 0)} testid="cs-tot-ordini" />
            <Kpi label="Tasso di chiusura" value={cstats.tasso_chiusura != null ? `${cstats.tasso_chiusura}%` : "N/D"} highlight testid="cs-tasso" />
            <Kpi label="Indice contrib. medio ordini" value={cstats.margine_contrib_medio != null ? cstats.margine_contrib_medio : "N/D"} highlight testid="cs-margine" />
            <Kpi label="% acquisto pannelli" value={cstats.pct_pannelli != null ? `${cstats.pct_pannelli}%` : "—"} testid="cs-pct-pannelli" />
            <Kpi label="% inverter e batterie" value={cstats.pct_inverter_batterie != null ? `${cstats.pct_inverter_batterie}%` : "—"} testid="cs-pct-inverter" />
            <Kpi label="% sistemi di fissaggio" value={cstats.pct_fissaggi != null ? `${cstats.pct_fissaggi}%` : "—"} testid="cs-pct-fissaggi" />
          </div>
        )}
      </Card>

      {p.is_partner && <VestaAccessCard partnerId={p.id} />}

      {p.is_partner && canExtra && <WebProfileCard partnerId={p.id} />}

      {p.is_partner && prenotazioni && prenotazioni.length > 0 && (
        <Card className="p-6" data-testid="partner-prenotazioni-card">
          <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold tracking-tight"><Gift className="h-5 w-5 text-[#E3B341]" /> Storico prenotazioni partner</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="py-2 pr-3 font-medium">Data</th>
                  <th className="py-2 pr-3 font-medium">Prodotto / Servizio</th>
                  <th className="py-2 pr-3 font-medium">Autore</th>
                  <th className="py-2 text-right font-medium text-[#E3B341]">Punti Vesta</th>
                </tr>
              </thead>
              <tbody>
                {prenotazioni.map((b) => (
                  <tr key={b.id} className="border-b border-border/50" data-testid={`prenotazione-row-${b.id}`}>
                    <td className="py-2 pr-3 text-muted-foreground">{b.data ? new Date(b.data).toLocaleDateString("it-IT") : "—"}</td>
                    <td className="py-2 pr-3 font-medium">{b.prodotto || "—"}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{b.autore}</td>
                    <td className="py-2 text-right font-mono font-semibold text-[#E3B341]">{(b.punti ?? 0).toLocaleString("it-IT", { maximumFractionDigits: 1 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="p-6" data-testid="profilazione-card">
          <h2 className="mb-1 font-heading text-lg font-bold tracking-tight">Scheda Cliente</h2>

          <SubHead>Anagrafica</SubHead>
          {p.region && <Row label="Territorio / Regione" value={p.region} />}
          {p.province && <Row label="Provincia" value={p.province} />}
          <Row label="Indirizzo" value={indirizzo} />
          <Row label="Città / CAP" value={anagCitta} />
          <div className="flex items-center justify-between border-b border-border py-2 text-sm">
            <span className="text-muted-foreground">Agente</span>
            {p.agency_active === false
              ? <span className="font-semibold text-red-600" data-testid="agente-inattivo">{p.agency_name ? `${p.agency_name} · ` : ""}Inattivo</span>
              : <span className="font-semibold" data-testid="agente-value">{p.agency_name ?? "—"}</span>}
          </div>
          <Row label="Partita IVA" value={p.piva} />
          <Row label="Codice Fiscale" value={p.codice_fiscale} />
          <Row label="Email" value={p.email} />
          {emailsExtra.length > 0 && <Row label="Email aggiuntive" value={emailsExtra.join(", ")} />}
          <Row label="Telefono" value={p.phone} />

          <SubHead>Dati societari</SubHead>
          {fd.data_costituzione && <Row label="Data di costituzione" value={itDate(fd.data_costituzione)} />}
          <Row label="Codice ATECO" value={ateco} />
          {fd.capitale_sociale != null && <Row label="Capitale sociale" value={eur(fd.capitale_sociale)} />}
          {fd.codice_sdi && <Row label="Codice SDI" value={fd.codice_sdi} />}
          <Row label="Anagrafica Creditsafe" value={csStato} />
          {(p.fascia_rischio || p.score != null) && <Row label="Valutazione Creditsafe" value={`${p.fascia_rischio || "—"}${p.score != null ? ` · score ${p.score}` : ""}`} />}
          {p.creditsafe_credit_limit != null && <Row label="Fido consigliato Creditsafe" value={eur(p.creditsafe_credit_limit)} />}
          {cs.status && cs.report_access === false && <Row label="Report Creditsafe" value="Solo anagrafica — utente API senza accesso ai report IT (403)" />}

          <SubHead>Dati commerciali</SubHead>
          <Row label="Classe Partner" value={p.partner_class} />
          <Row label="Potenziale acquisti annuo" value={p.potenziale_annuo != null ? eur(p.potenziale_annuo) : "—"} />
          <Row label="Acquisti TORRI anno" value={p.acquisti_torri_anno != null ? eur(p.acquisti_torri_anno) : "—"} />
          <Row label="Share of Wallet TORRI" value={sow === "N/D" || sow == null ? "N/D" : `${sow}%`} />
          {fd.livello_partner && <Row label="Livello partner" value={fd.livello_partner} />}
          <Row label="N° preventivi (gestionale)" value={p.num_preventivi != null ? num(p.num_preventivi) : "—"} />
          <Row label="N° ordini (gestionale)" value={p.num_ordini != null ? num(p.num_ordini) : "—"} />
          {fd.fascia_rischio && <Row label="Fascia di rischio" value={fd.fascia_rischio} />}
          {fd.credito_assicurato != null && <Row label="Credito assicurato" value={eur(fd.credito_assicurato)} />}
          {fd.modalita_pagamento && <Row label="Modalità di pagamento" value={fd.modalita_pagamento} />}
          {fd.fido_massimo != null && <Row label="Fido massimo concesso" value={eur(fd.fido_massimo)} />}
          <Row label="Ultimo contatto" value={p.ultimo_contatto} />
          <Row label="Ultima visita" value={p.ultima_visita} />
          <Row label="Prossima azione" value={p.prossima_azione} />
          <Row label="Data prossima azione" value={p.data_prossima_azione} />

          <SubHead>Partnership · Rete Beyond</SubHead>
          <Row label="Data di attivazione" value={fd.data_attivazione ? itDate(fd.data_attivazione) : "—"} />
          <div className="flex items-center justify-between gap-3 border-b border-border py-2 text-sm">
            <span className="text-muted-foreground">Profilo Web Partner</span>
            {fd.profilo_web
              ? <a href={fd.profilo_web} target="_blank" rel="noreferrer" className="truncate font-semibold text-primary hover:underline" data-testid="profilo-web-link">{fd.profilo_web.replace(/^https?:\/\//, "")}</a>
              : <span className="font-semibold">—</span>}
          </div>
          {p.is_partner && (
            <div className="flex items-center justify-between border-b border-border py-2 text-sm">
              <span className="flex items-center gap-1 text-[#E3B341]"><VestaBadge /> punti Vesta <VestaInfo /> <span className="text-[10px] text-muted-foreground/70">(ordini post-attivazione)</span></span>
              <span className="rounded-full bg-[#E3B341]/15 px-2 py-0.5 text-[11px] font-bold text-[#E3B341]" data-testid="punti-vesta">{cstats?.vesta_punti != null ? cstats.vesta_punti.toLocaleString("it-IT", { maximumFractionDigits: 1 }) : "—"}</span>
            </div>
          )}
          <div className="border-b border-border py-2 text-sm">
            <div className="mb-1 flex items-center justify-between gap-2">
              <span className="text-muted-foreground">Contratto partnership (firmato)</span>
              {canConvert && (
                <Button variant="outline" size="sm" className="h-7" onClick={() => setConvDlg(p)} data-testid="convert-partner-detail-btn">
                  <Handshake className="mr-2 h-3.5 w-3.5 text-green-600" /> {p.is_partner ? "Rinnova contratto" : "Converti in Partner"}
                </Button>
              )}
            </div>
            {p.partnership_contract ? (() => {
              const c = p.partnership_contract;
              const days = c.data_scadenza ? Math.ceil((new Date(c.data_scadenza + "T00:00:00") - new Date()) / 86400000) : null;
              return (
                <div className="space-y-1" data-testid="partnership-contract-info">
                  <div className="flex flex-wrap items-center gap-2">
                    <Button variant="outline" size="sm" className="h-7" onClick={() => scaricaContrattoFirmato(c.id, c.filename)} data-testid="download-contratto-firmato-btn"><Download className="mr-2 h-3.5 w-3.5" /> Scarica PDF</Button>
                    {days != null && days < 0 && <Badge className="bg-red-600 text-white" data-testid="contratto-scaduto-badge">Scaduto — da rinnovare</Badge>}
                    {days != null && days >= 0 && days <= 30 && <Badge className="bg-amber-500 text-white" data-testid="contratto-in-scadenza-badge">In scadenza ({days} gg)</Badge>}
                    {days != null && days > 30 && <Badge className="bg-green-600 text-white">Attivo</Badge>}
                  </div>
                  <p className="text-[11px] text-muted-foreground">Firma {itDate(c.data_firma)} · Scadenza {itDate(c.data_scadenza)}{c.uploaded_by ? ` · caricato da ${c.uploaded_by}` : ""}</p>
                  {(p.partnership_contracts || []).length > 1 && <p className="text-[11px] text-muted-foreground">Storico: {(p.partnership_contracts || []).length} contratti archiviati</p>}
                </div>
              );
            })() : fd.contratto_url ? (
              <Button variant="outline" size="sm" className="h-7" onClick={scaricaContratto} data-testid="download-contratto-btn"><Download className="mr-2 h-3.5 w-3.5" /> Scarica PDF (gestionale)</Button>
            ) : <span className="font-semibold">—</span>}
            {p.is_partner && !p.partnership_contract && <Badge className="mt-1 inline-block bg-red-600 text-white text-[10px]" data-testid="contratto-mancante-badge">Contratto scaduto / mancante — da caricare</Badge>}
          </div>

          {p.is_partner && (
            <div className="flex items-center justify-between border-b border-border py-2 text-sm" data-testid="partner-categoria-row">
              <span className="text-muted-foreground">Categoria partner</span>
              <Select value={p.partner_categoria || ""} onValueChange={async (v) => { try { await api.put(`/partners/${p.id}/categoria`, { categoria: v }); toast.success("Categoria aggiornata"); loadPartner(); } catch { toast.error("Salvataggio non riuscito"); } }} disabled={!canConvert}>
                <SelectTrigger className="h-7 w-44" data-testid="partner-categoria-select"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Installatore">Installatore</SelectItem>
                  <SelectItem value="Progettista">Progettista</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <SubHead>Altro</SubHead>
          {["admin", "head_of_partner", "back_office", "commercial_manager"].includes(user?.role) && <div className="mb-2"><AiProfiloCrm p={p} onSaved={loadPartner} /></div>}
          {(p.website || p.descrizione || p.profilo_vesta_at) && (
            <div className="mb-2 space-y-1 text-sm" data-testid="partner-profilo-vesta">
              {p.website && <div><span className="text-muted-foreground">Sito web: </span><a href={/^https?:\/\//.test(p.website) ? p.website : `https://${p.website}`} target="_blank" rel="noreferrer" className="font-semibold underline" data-testid="partner-website">{p.website}</a></div>}
              {p.descrizione && <p className="whitespace-pre-line rounded-md border border-border p-3 text-xs" data-testid="partner-descrizione">{p.descrizione}</p>}
              <ProfiloExtra p={p} />
              {p.profilo_vesta_at && <div className="text-[11px] text-muted-foreground" data-testid="partner-profilo-vesta-at">Profilo aggiornato dal Partner su Ai VESTA il {new Date(p.profilo_vesta_at).toLocaleString("it-IT")}{p.profilo_vesta_by ? ` · ${p.profilo_vesta_by}` : ""}</div>}
            </div>
          )}
          {p.note_commerciali && <p className="rounded-md bg-accent p-3 text-xs text-accent-foreground">{p.note_commerciali}</p>}
          {p.fier_dettaglio_at && <div className="mt-2 text-[11px] text-muted-foreground">Dati gestionale aggiornati il {itDate(p.fier_dettaglio_at)}</div>}
          {!p.note_commerciali && !p.fier_dettaglio_at && !p.website && !p.descrizione && <p className="py-2 text-sm text-muted-foreground">—</p>}
        </Card>

        <Card className="p-6" data-testid="presidio-card">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Gauge className="h-4 w-4 text-primary" />
              <h2 className="font-heading text-lg font-bold tracking-tight">Presidio Commerciale</h2>
            </div>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="h-9 w-[140px]" data-testid="presidio-period"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="mese">Mese</SelectItem>
                <SelectItem value="trimestre">Trimestre</SelectItem>
                <SelectItem value="anno">Anno</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {!presidio ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : (
            <>
              <div className="mb-4 flex items-center gap-3">
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Stato presidio</span>
                <Badge className={`border ${STATO_STYLE[presidio.stato]}`} data-testid="presidio-stato">{presidio.stato}</Badge>
                {presidio.coverage != null && <span className="text-sm text-muted-foreground">{presidio.coverage}% copertura</span>}
              </div>
              <Row label="Classe Partner" value={presidio.partner_class} />
              <Row label="Visite previste" value={presidio.visite_previste ?? "N/D"} />
              <Row label="Visite effettuate" value={num(presidio.visite_effettuate)} />
              <Row label="Contatti previsti" value={presidio.contatti_previste ?? "N/D"} />
              <Row label="Contatti effettuati" value={num(presidio.contatti_effettuati)} />
              <Row label="Giorni dall'ultima visita" value={presidio.giorni_dall_ultima_visita ?? "Mai"} />
              <Row label="Prossima azione" value={presidio.prossima_azione} />
              <Row label="Data prossima azione" value={presidio.data_prossima_azione} />
            </>
          )}
        </Card>
      </div>

      {canAff && (
        <Card className="p-6" data-testid="partner-affidamento-card">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2"><Wallet className="h-4 w-4 text-primary" /><h2 className="font-heading text-lg font-bold tracking-tight">Fido & Ultimo bilancio</h2></div>
            <Button variant="outline" size="sm" onClick={enrichCreditsafe} disabled={csEnriching} data-testid="cs-enrich-btn">
              {csEnriching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} Arricchisci da Creditsafe
            </Button>
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Fido massimo concesso (€)</label>
            <div className="mt-1 flex max-w-md gap-2">
              <Input type="number" value={affVal} onChange={(e) => setAffVal(e.target.value)} data-testid="aff-input" placeholder={fd.fido_massimo != null ? `Gestionale: ${fd.fido_massimo}` : "—"} />
              <Button onClick={saveAffidamento} disabled={affSaving} data-testid="aff-save">{affSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salva</Button>
            </div>
            {fd.fido_massimo != null && <p className="mt-1 text-[11px] text-muted-foreground">Da gestionale: {eur(fd.fido_massimo)} — la modifica manuale ha priorità e non viene sovrascritta dal sync.</p>}
            <div className="mt-4 border-t border-border pt-4" data-testid="partner-pagamento-default">
              <label className="text-xs font-semibold text-muted-foreground">Modalità di pagamento (default anagrafica)</label>
              <div className="mt-1 flex max-w-md items-center gap-2">
                <Select value={payDefault} onValueChange={savePayDefault} disabled={paySaving}>
                  <SelectTrigger className="w-full" data-testid="partner-pagamento-select"><SelectValue placeholder="Ad avviso merce pronta (default globale)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Default globale (Ad avviso merce pronta) —</SelectItem>
                    {payMethods.map((m) => <SelectItem key={m.id} value={m.name} data-testid={`partner-pag-opt-${m.id}`}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {paySaving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">L'agente nel preventivo la vedrà pre-selezionata (già approvata). Se ne sceglie un'altra, il preventivo passa dal Back Office.</p>
            </div>
            <div className="mt-3 space-y-2" data-testid="fido-suggeriti">
              <div className="text-xs font-semibold text-muted-foreground">Fido suggerito — puoi usare bilancio e/o Creditsafe</div>
              {(fidoSug?.bilancio || fidoSug?.creditsafe) ? (
                <div className="grid gap-2 sm:grid-cols-2">
                  <FidoBox titolo="bilancio" sug={fidoSug?.bilancio} onApply={applyFido} applying={affSaving} />
                  <FidoBox titolo="creditsafe" sug={fidoSug?.creditsafe} onApply={applyFido} applying={affSaving} />
                </div>
              ) : (
                <div className="flex items-center gap-2 text-[11px] font-semibold text-amber-600 dark:text-amber-400" data-testid="fido-no-fonti">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Carica un bilancio PDF oppure arricchisci da Creditsafe per calcolare il fido.
                </div>
              )}
              {fidoSug?.bilancio && fidoSug?.creditsafe && (
                <p className="text-[11px] text-muted-foreground">Entrambe le valutazioni sono attive: valuta il valore più prudente. Per SNC/ditte individuali con fido richiesto alto, chiedi il bilancio come verifica extra.</p>
              )}
              <div className="mt-2 rounded-lg border border-border bg-muted/30 p-3" data-testid="cs-manual-form">
                <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Dati Creditsafe (inserimento manuale)</div>
                <div className="grid gap-2 sm:grid-cols-4">
                  {[["score", "Score Creditsafe"], ["fido_consigliato", "Fido consigliato (€)"], ["data_fondazione", "Data di fondazione"], ["dipendenti", "Dipendenti"]].map(([k, lab]) => (
                    <div key={k}>
                      <label className="text-[11px] font-semibold text-muted-foreground">{lab}</label>
                      <Input className="mt-1" type={k === "data_fondazione" ? "date" : "number"} value={csMan[k] ?? ""} onChange={(e) => setCsMan((s) => ({ ...s, [k]: e.target.value }))} data-testid={`cs-man-${k}`} />
                    </div>
                  ))}
                </div>
                <div className="mt-2 flex justify-end"><Button size="sm" onClick={saveCsManual} disabled={csManSaving} data-testid="cs-man-save">{csManSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salva dati Creditsafe</Button></div>
              </div>
            </div>
          </div>
          <div className="mt-5 mb-2 flex flex-wrap items-center justify-between gap-2">
            <SubHead>Ultimo bilancio (acquisito automaticamente da PDF)</SubHead>
            <input ref={bilFileRef} type="file" accept="application/pdf" className="hidden" data-testid="bil-upload-input"
              onChange={(e) => { uploadBilancio(e.target.files?.[0]); e.target.value = ""; }} />
            <Button size="sm" disabled={bilUploading} onClick={() => bilFileRef.current?.click()} data-testid="bil-upload-btn">
              {bilUploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}Carica bilancio (PDF)
            </Button>
          </div>
          {(() => {
            const b = p.bilancio || {};
            const fd2 = p.fier_dettaglio || {};
            const personale = b.costo_personale != null
              ? <>{eur(b.costo_personale)} <span className="text-[11px] font-normal text-muted-foreground">(≈ {b.stima_dipendenti ?? Math.round(b.costo_personale / 40000)} pers.)</span></>
              : "—";
            const items = [
              ["Anno", b.anno ?? fd2.anno_bilancio, "plain"],
              ["Fatturato", b.fatturato ?? fd2.fatturato],
              ["Patrimonio netto", b.patrimonio],
              ["Magazzino", b.magazzino],
              ["Costo del personale", personale, "node"],
              ["Utile ante imposte", b.utile_ante_imposte],
              ["Ammortamenti", b.ammortamenti],
              ["Oneri finanziari", b.oneri_finanziari],
              ["EBITDA (calcolato)", b.ebitda],
              ["Debiti oltre 12 mesi", b.debiti_oltre_12m],
              ["Disponibilità liquide", b.disponibilita_liquide],
              ["PFN (calcolata)", b.pfn],
            ];
            return (
              <div className="grid gap-3 sm:grid-cols-3" data-testid="bil-readonly">
                {items.map(([label, val, type]) => (
                  <div key={label} className="rounded-md border border-border p-2">
                    <div className="text-[11px] font-semibold text-muted-foreground">{label}</div>
                    <div className="text-sm font-semibold">{type === "node" ? val : (val == null || val === "" ? "—" : (type === "plain" ? val : eur(val)))}</div>
                  </div>
                ))}
              </div>
            );
          })()}
          {p.bilancio?.updated_at && <p className="mt-2 text-[11px] text-muted-foreground">Acquisito il {itDate(p.bilancio.updated_at)}{p.bilancio.filename ? ` dal PDF «${p.bilancio.filename}»` : ""}{p.bilancio.updated_by ? ` · ${p.bilancio.updated_by}` : ""}</p>}
          {(p.bilanci_archivio?.length > 0) && (
            <div className="mt-4">
              <SubHead>Archivio bilanci ({p.bilanci_archivio.length})</SubHead>
              <div className="space-y-1">
                {p.bilanci_archivio.map((e) => (
                  <div key={e.id} className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm" data-testid="bil-archivio-row">
                    <div className="flex min-w-0 items-center gap-2">
                      <FileText className="h-4 w-4 shrink-0 text-primary" />
                      <span className="truncate">{e.filename}</span>
                      {e.anno && <Badge variant="secondary" className="text-[10px]">{e.anno}</Badge>}
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <span className="text-[11px] text-muted-foreground">{itDate(e.uploaded_at)}</span>
                      <button onClick={() => downloadBilancio(e.id)} className="text-primary hover:underline" title="Scarica" data-testid="bil-download-btn"><Download className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      <QuotesForClient partnerId={id} testid="partner-crm-quotes" />

      <ConsulenzeAnagrafica installerId={id} testid="partner-consulenze" />
      <ConsulenzeTecnicheAnagrafica installerId={id} testid="partner-consulenze-tecniche" />

      <ActivityLog url={`/partners/${id}/activities`} refreshKey={refreshKey} title="Attività svolte sul cliente" testid="partner-activity-log" />

      <Card id="mappa-pin" className="p-6" data-testid="partner-pin-card">
        <div className="mb-3 flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /><h2 className="font-heading text-lg font-bold tracking-tight">Posizione sulla mappa</h2></div>
        <p className="mb-3 text-xs text-muted-foreground" data-testid="pin-status">
          {pin.lat != null ? <>Posizione attuale: <span className="font-mono">{(+pin.lat).toFixed(5)}, {(+pin.lng).toFixed(5)}</span>{p.geo_manual ? " · 📍 fissata manualmente" : " · da indirizzo"}</> : "Nessuna posizione impostata"}
        </p>
        <PinPicker lat={pin.lat != null ? +pin.lat : null} lng={pin.lng != null ? +pin.lng : null} onSet={(la, ln) => setPin({ lat: la, lng: ln })} />
        <div className="mt-3 flex items-center gap-2">
          <Button size="sm" onClick={savePin} disabled={pinSaving || pin.lat == null} data-testid="pin-save-btn">{pinSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <MapPin className="mr-2 h-4 w-4" />}Salva posizione</Button>
          <span className="text-[11px] text-muted-foreground">Trascina o clicca il segnaposto per fissare la posizione esatta.</span>
        </div>
      </Card>

      <ConvertPartnerDialog partner={convDlg} onOpenChange={(o) => { if (!o) setConvDlg(null); }} onDone={() => { setConvDlg(null); loadPartner(); }} />
    </div>
  );
}
